import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import ScrollAnimation from '../../motion/ScrollAnimation'
import EmailSender from '../../../api/emailsender'

function ContactPageMain() {
    const orgTypes = useMemo(
        () => [
            'Healthcare Practice or Clinic',
            'Hospital or Health System',
            'Technology or SaaS Company',
            'Financial Services or Fintech',
            'Legal or Professional Services',
            'Retail or E-Commerce',
            'Education or Coaching',
            'Author or Publishing',
            'Manufacturing or Industrial',
            'Other',
        ],
        []
    )

    const orgSizes = useMemo(
        () => [
            'Solo or Freelance',
            '2 to 10 people',
            '11 to 50 people',
            '51 to 200 people',
            '201 to 500 people',
            '500 or more',
        ],
        []
    )

    const basedWhere = useMemo(
        () => ['US East Coast', 'US Midwest', 'US South', 'US West Coast', 'US Other', 'Outside the United States'],
        []
    )

    const servicesInterested = useMemo(
        () => [
            'Medical Billing & Revenue Cycle Management',
            'Cybersecurity Services',
            'Call Center & Customer Support Outsourcing',
            'Ebook, Whitepaper or Book Writing',
            'Not Sure Yet, I Need Guidance',
        ],
        []
    )

    const biggestChallenges = useMemo(
        () => [
            'Too many claims being denied or written off',
            'Revenue cycle is slow and cash flow is suffering',
            'Worried about a security breach or compliance gap',
            'Customers or patients are not being reached properly',
            'Need professional content to build authority or publish',
            'Multiple challenges across operations',
            'Not sure yet — just exploring options',
        ],
        []
    )

    const triedBefore = useMemo(
        () => [
            'No — first time looking for a solution',
            "Yes — tried handling it in-house, didn't work",
            "Yes — used another company, didn't work out",
            'Yes — tried multiple approaches with mixed results',
        ],
        []
    )

    const timeline = useMemo(
        () => ['Urgently — within 2 weeks', 'Soon — within a month', 'Planning ahead — 2 to 3 months', 'Just exploring options'],
        []
    )

    const contactMethod = useMemo(() => ['Phone call', 'Email', 'Either — whichever is faster'], [])

    const bestTime = useMemo(
        () => [
            'Morning 8 AM–12 PM EST',
            'Afternoon 12–4 PM EST',
            'Late Afternoon 4–6 PM EST',
            'Saturday 9 AM–2 PM EST',
            'Anytime — I’m flexible',
        ],
        []
    )

    const foundUs = useMemo(() => ['Google Search', 'Referral', 'LinkedIn', 'Social Media', 'Other'], [])

    const [consent, setConsent] = useState(false)
    const [servicesError, setServicesError] = useState(false)
    const [selectedServices, setSelectedServices] = useState(() => new Set())
    const { isSubmitting, succeeded, error, sendEmail, setSucceeded, setError } = EmailSender()

    const toggleService = (label) => {
        setSelectedServices((prev) => {
            const next = new Set(prev)
            if (next.has(label)) next.delete(label)
            else next.add(label)
            return next
        })
    }

    const onSubmit = async (e) => {
        e.preventDefault()
        setSucceeded(false)
        setError(null)

        const hasService = selectedServices.size > 0
        setServicesError(!hasService)
        if (!hasService) {
            const el = document.getElementById('td-contact-form')
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
            return
        }

        const form = e.currentTarget
        const getChecked = (name) => {
            const el = form.querySelector(`input[name="${name}"]:checked`)
            return el ? el.value : ''
        }
        const getOptional = (name) => {
            const el = form.querySelector(`input[name="${name}"]:checked`)
            return el ? el.value : 'Not selected'
        }

        const formData = {
            'First name': form.firstName.value.trim(),
            'Last name': form.lastName.value.trim(),
            Email: form.email.value.trim(),
            Phone: form.phone.value.trim(),
            'Organization type': getChecked('orgType'),
            'Organization size': getChecked('orgSize'),
            'Based where': getChecked('basedWhere'),
            'Services interested in': Array.from(selectedServices).join(', '),
            'Biggest challenge': getChecked('biggestChallenge'),
            'Tried before': getChecked('triedBefore'),
            Timeline: getChecked('timeline'),
            'Preferred contact method': getOptional('contactMethod'),
            'Best time to reach': getOptional('bestTime'),
            'How they found us': getOptional('foundUs'),
            'Additional information': form.additionalInfo ? form.additionalInfo.value.trim() : '',
            'Privacy consent': consent ? 'Yes' : 'No',
        }

        const ok = await sendEmail(formData, 'TechDigit — New contact form submission')
        if (ok) {
            form.reset()
            setSelectedServices(new Set())
            setConsent(false)
            setServicesError(false)
            const el = document.getElementById('td-contact-form')
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }

    return (
        <>
            <ScrollAnimation direction="up" delay={0.06} duration={0.5}>
                <section className="td-section rts-section-gap td-contact-hero">
                    <div className="container">
                        <div className="td-contact-hero__panel">
                            <div className="row align-items-center g-4 g-lg-5">
                                <div className="col-lg-6">
                                    <div className="td-contact-hero__kicker">Contact Us</div>
                                    <h2 className="td-contact-hero__title">Let’s Talk About What You’re Trying to Fix</h2>
                                    <p className="td-contact-hero__lead">
                                        No sales pitch. No pressure. Just an honest conversation about your challenges and
                                        whether TechDigit Solutions is the right team to solve them.
                                    </p>
                                </div>
                                <div className="col-lg-6">
                                    <div className="td-contact-hero__visual">
                                        <img
                                            src="/assets/images/about/industriesnew.jpeg"
                                            alt="Contact TechDigit Solutions"
                                            loading="lazy"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </ScrollAnimation>

            <ScrollAnimation direction="fade" delay={0.04} duration={0.42}>
                <section className="td-section rts-section-gap td-contact-strip">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-10 col-xl-9 text-center">
                                <h2 className="td-section-title">
                                    The First Conversation Is Always Free — And Always Useful
                                </h2>
                                <div className="td-title-rule td-title-rule--center mx-auto" aria-hidden="true" />
                                <p className="disc mt-3">
                                    You don’t need to have everything figured out before you reach out. Most people who
                                    contact us just know something isn’t working: too many denied claims, recurring
                                    security concerns, customers not getting through, or a content project that never gets
                                    finished.
                                </p>
                                <p className="disc">Show up to the conversation. We’ll take it from there.</p>
                                <div className="td-contact-strip__meta">
                                    <a href="tel:+18252516570">+1 (825) 251-6570</a>
                                    <span className="td-contact-strip__meta-sep" aria-hidden="true">
                                        &nbsp;·&nbsp;
                                    </span>
                                    <a href="mailto:info@techdigitsolutions.com">info@techdigitsolutions.com</a>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </ScrollAnimation>

            {/* <ScrollAnimation direction="up" delay={0.06} duration={0.48}> */}
                <section id="td-contact-form" className="td-section rts-section-gap td-contact-form td-contact-form--full">
                    <div className="container-fluid td-contact-form--container">
                        <div className="row">
                            <div className="col-12">
                                <div className="td-section-head text-center mb-4 mb-lg-5">
                                    <p className="td-section-label">Contact Form</p>
                                    <h2 className="td-section-title">Tell Us About Your Situation</h2>
                                    <div className="td-title-rule td-title-rule--center mx-auto" aria-hidden="true" />
                                </div>

                                <div className="td-contact-form__panel">
                                    {succeeded && (
                                        <div className="alert alert-success mb-4" role="status">
                                            Thank you. Your message was sent — we will get back to you within one business day
                                            (usually the same day).
                                        </div>
                                    )}
                                    {error && (
                                        <div className="alert alert-danger mb-4" role="alert">
                                            {error}
                                        </div>
                                    )}
                                    <form onSubmit={onSubmit}>
                                        <div className="td-contact-form__section-title">About You</div>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="td-contact-form__group">
                                                    <label className="td-contact-form__label" htmlFor="firstName">
                                                        First Name *
                                                    </label>
                                                    <input
                                                        id="firstName"
                                                        name="firstName"
                                                        className="td-contact-form__control"
                                                        type="text"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="td-contact-form__group">
                                                    <label className="td-contact-form__label" htmlFor="lastName">
                                                        Last Name *
                                                    </label>
                                                    <input
                                                        id="lastName"
                                                        name="lastName"
                                                        className="td-contact-form__control"
                                                        type="text"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="td-contact-form__group">
                                                    <label className="td-contact-form__label" htmlFor="email">
                                                        Email Address *
                                                    </label>
                                                    <input
                                                        id="email"
                                                        name="email"
                                                        className="td-contact-form__control"
                                                        type="email"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="td-contact-form__group">
                                                    <label className="td-contact-form__label" htmlFor="phone">
                                                        Phone Number *
                                                    </label>
                                                    <input
                                                        id="phone"
                                                        name="phone"
                                                        className="td-contact-form__control"
                                                        type="tel"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="td-contact-form__section-title">About Your Organization</div>
                                        <div className="td-contact-form__group">
                                            <div className="td-contact-form__label">What type of organization are you? *</div>
                                            <div className="td-contact-form__options">
                                                {orgTypes.map((label) => (
                                                    <label key={label} className="td-contact-form__option">
                                                        <input type="radio" name="orgType" value={label} required />
                                                        <span>{label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="td-contact-form__group">
                                            <div className="td-contact-form__label">How large is your organization? *</div>
                                            <div className="td-contact-form__options">
                                                {orgSizes.map((label) => (
                                                    <label key={label} className="td-contact-form__option">
                                                        <input type="radio" name="orgSize" value={label} required />
                                                        <span>{label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="td-contact-form__group">
                                            <div className="td-contact-form__label">Where are you based? *</div>
                                            <div className="td-contact-form__options">
                                                {basedWhere.map((label) => (
                                                    <label key={label} className="td-contact-form__option">
                                                        <input type="radio" name="basedWhere" value={label} required />
                                                        <span>{label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="td-contact-form__section-title">What You Need</div>
                                        <div className="td-contact-form__group">
                                            <div className="td-contact-form__label">Which service are you interested in? *</div>
                                            <div className="td-contact-form__options">
                                                {servicesInterested.map((label) => (
                                                    <label key={label} className="td-contact-form__option">
                                                        <input
                                                            type="checkbox"
                                                            name="servicesInterested"
                                                            value={label}
                                                            checked={selectedServices.has(label)}
                                                            onChange={() => toggleService(label)}
                                                        />
                                                        <span>{label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                            <p className="td-contact-form__hint">
                                                {servicesError ? 'Please select at least one service.' : 'Select at least one.'}
                                            </p>
                                        </div>

                                        <div className="td-contact-form__group">
                                            <div className="td-contact-form__label">What is your biggest challenge right now? *</div>
                                            <div className="td-contact-form__options">
                                                {biggestChallenges.map((label) => (
                                                    <label key={label} className="td-contact-form__option">
                                                        <input type="radio" name="biggestChallenge" value={label} required />
                                                        <span>{label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="td-contact-form__group">
                                            <div className="td-contact-form__label">Have you tried to solve this before? *</div>
                                            <div className="td-contact-form__options">
                                                {triedBefore.map((label) => (
                                                    <label key={label} className="td-contact-form__option">
                                                        <input type="radio" name="triedBefore" value={label} required />
                                                        <span>{label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="td-contact-form__section-title">Your Timeline</div>
                                        <div className="td-contact-form__group">
                                            <div className="td-contact-form__label">How soon do you need a solution? *</div>
                                            <div className="td-contact-form__options">
                                                {timeline.map((label) => (
                                                    <label key={label} className="td-contact-form__option">
                                                        <input type="radio" name="timeline" value={label} required />
                                                        <span>{label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="td-contact-form__group">
                                            <div className="td-contact-form__label">Preferred contact method</div>
                                            <div className="td-contact-form__options">
                                                {contactMethod.map((label) => (
                                                    <label key={label} className="td-contact-form__option">
                                                        <input type="radio" name="contactMethod" value={label} />
                                                        <span>{label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="td-contact-form__group">
                                            <div className="td-contact-form__label">Best time to reach you</div>
                                            <div className="td-contact-form__options">
                                                {bestTime.map((label) => (
                                                    <label key={label} className="td-contact-form__option">
                                                        <input type="radio" name="bestTime" value={label} />
                                                        <span>{label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="td-contact-form__group">
                                            <div className="td-contact-form__label">How did you find us?</div>
                                            <div className="td-contact-form__options">
                                                {foundUs.map((label) => (
                                                    <label key={label} className="td-contact-form__option">
                                                        <input type="radio" name="foundUs" value={label} />
                                                        <span>{label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="td-contact-form__section-title">Additional Information</div>
                                        <div className="td-contact-form__group">
                                            <label className="td-contact-form__label" htmlFor="additionalInfo">
                                                Anything else we should know: current challenges, previous outsourcing experiences,
                                                specific questions or anything else on your mind?
                                            </label>
                                            <textarea
                                                id="additionalInfo"
                                                name="additionalInfo"
                                                className="td-contact-form__control"
                                                rows={6}
                                            />
                                        </div>

                                        <div className="td-contact-form__consent">
                                            <label className="td-contact-form__option" style={{ margin: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    name="consent"
                                                    checked={consent}
                                                    onChange={(e) => setConsent(e.target.checked)}
                                                    required
                                                />
                                                <span>
                                                    Required — I have read and agree to the TechDigit Solutions{' '}
                                                    <Link to="/privacy-policy">Privacy Policy</Link> and{' '}
                                                    <Link to="/terms-and-conditions">Terms and Conditions</Link>. I understand my
                                                    information will be used solely to respond to my inquiry and arrange a
                                                    consultation.
                                                </span>
                                            </label>
                                            <p className="td-contact-form__hint" style={{ marginTop: 10, marginBottom: 0 }}>
                                                Your information is never sold or shared with third parties.
                                            </p>
                                        </div>

                                        <div className="td-contact-form__submit-row">
                                            <button
                                                type="submit"
                                                className="rts-btn btn-primary"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? 'Sending…' : 'Schedule My Free Consultation →'}
                                            </button>
                                            <p className="td-contact-form__fineprint">
                                                We respond within one business day, usually the same day.
                                            </p>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            {/* </ScrollAnimation> */}

            <ScrollAnimation direction="up" delay={0.06} duration={0.48}>
                <section className="td-section rts-section-gap">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-10 col-xl-9">
                                <div className="td-section-head text-center mb-4 mb-lg-5">
                                    <p className="td-section-label">What Happens Next</p>
                                    <h2 className="td-section-title">A Simple Process. No Pressure.</h2>
                                    <div className="td-title-rule td-title-rule--center mx-auto" aria-hidden="true" />
                                </div>
                                <div className="row g-4">
                                    <div className="col-md-4">
                                        <div className="pillar-card h-100">
                                            <div className="pillar-card__header">
                                                <div className="pillar-card__head-main">
                                                    <div className="pillar-heading">
                                                        <span className="pillar-number">01</span>
                                                        <h4 className="pillar-title">We Review What You Shared</h4>
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="pillar-text">
                                                We read everything before the call. No repeating yourself. We show up prepared.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="pillar-card h-100">
                                            <div className="pillar-card__header">
                                                <div className="pillar-card__head-main">
                                                    <div className="pillar-heading">
                                                        <span className="pillar-number">02</span>
                                                        <h4 className="pillar-title">Free 30 to 45 Minute Consultation</h4>
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="pillar-text">
                                                Honest conversation. Real assessment. We’ll tell you what we think, including if
                                                we’re not the right fit.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="pillar-card h-100">
                                            <div className="pillar-card__header">
                                                <div className="pillar-card__head-main">
                                                    <div className="pillar-heading">
                                                        <span className="pillar-number">03</span>
                                                        <h4 className="pillar-title">You Decide</h4>
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="pillar-text">
                                                Zero pressure. Clear proposal. Realistic timelines. Transparent pricing. Your
                                                decision on your timeline.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </ScrollAnimation>

            {/* <section className="td-section rts-section-gap td-contact-row" aria-label="Contact row">
                <div className="container">
                    <div className="td-contact-row__inner">
                        <div className="td-contact-row__item">
                            <span className="td-contact-row__icon" aria-hidden="true">
                                <i className="fas fa-envelope" />
                            </span>
                            <span className="td-contact-row__label">Email</span>
                            <a href="mailto:info@techdigitsolutions.com" className="td-contact-row__value">
                                info@techdigitsolutions.com
                            </a>
                        </div>

                        <div className="td-contact-row__item td-contact-row__item--urgent">
                            <span className="td-contact-row__icon" aria-hidden="true">
                                <i className="fas fa-shield-alt" />
                            </span>
                            <span className="td-contact-row__label">Active Security Incident?</span>
                            <span className="td-contact-row__value td-contact-row__value--static">
                                Email info@techdigitsolutions.com with URGENT in the subject — we respond as quickly as we can.
                            </span>
                        </div>
                    </div>
                </div>
            </section> */}

            {/* <ScrollAnimation direction="up" delay={0.06} duration={0.48}>
                <section className="td-section rts-section-gap td-contact-strip">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-10 col-xl-9 text-center">
                                <div className="td-contact-strip__meta" style={{ marginTop: 0 }}>
                                    <span>Founded 2020</span>
                                    <span aria-hidden="true">|</span>
                                    <span>95%+ Retention</span>
                                    <span aria-hidden="true">|</span>
                                    <span>25+ Clients</span>
                                    <span aria-hidden="true">|</span>
                                    <span>Free Consultation</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </ScrollAnimation> */}

            {/* <ScrollAnimation direction="down" delay={0.05} duration={0.44}>
                <section className="td-section rts-section-gap">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-10 col-xl-9 text-center">
                                <h2 className="td-section-title">Something Isn’t Working. Let’s Fix It.</h2>
                                <div className="td-title-rule td-title-rule--center mx-auto" aria-hidden="true" />
                                <p className="disc mt-3">One free conversation. Thirty minutes. Clarity either way.</p>
                                <p className="disc">
                                    <a href="#td-contact-form">Fill Out the Form Above</a>
                                </p>
                                <p className="disc">
                                    <a href="mailto:info@techdigitsolutions.com">info@techdigitsolutions.com</a>
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </ScrollAnimation> */}

        </>
    )
}

export default ContactPageMain
